/**
 * LandingPage Component
 *
 * Main landing page: question assistant, workspace lists, and tutorial discovery.
 */

// ============= Imports =============

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  QuestionAssistant,
  Spacer,
  LoadingSpinner,
  WorkspaceList,
} from '@tw/tw-component-library';
import { UseAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import {
  landingPageMockData,
  guidedTutorials,
} from '@/mockData/landingPageMockData';
import { CreateWorkspace } from '../../../components/container/createWorkspace/CreateWorkspace';
import {
  AssistantFeatureCard,
  ContentSection,
  ContentSectionContainer,
  PageContainer,
  QuestionAssistantContainer,
  SuggestedActionProgressStrip,
  WorkspaceSection,
} from './Landingpage.styles';
import {
  CREATE_WORKSPACE_ACTION,
  LANDING_PAGE_TUTORIAL_SESSION_STORAGE_KEY,
  WORKSPACE_CREATE_ROUTE,
} from './Landingpage.constants';
import type {
  ExtendedQuestionAssistantProps,
  Tutorial,
} from './Landingpage.types';
import { getFirstGreetingName } from './Landingpage.utils';
import { useLandingPageTheme } from './LandingPage.useTheme';
import { useLandingPageHistory } from './LandingPage.useHistory';
import { useLandingPageWorkspaceActivity } from './LandingPage.useWorkspaceActivity';
import { useLandingPageWhatToDo } from './LandingPage.useWhatToDo';
import { useLandingPageSessionTemplate } from './LandingPage.useSessionTemplate';
import { useLandingPageTutorials } from './LandingPage.useTutorials';
import { LandingPageSessionTemplateModal } from './LandingPage.SessionTemplateModal';
import { LandingPageResultOptionsChips } from './LandingPage.ResultOptionsChips';

// ============= Component =============

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessToken, currentUser } = UseAuth();
  const { handleError } = useWorkspaceErrorHandler();
  const { themeMode, isCompactTheme } = useLandingPageTheme();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const questionAssistantRef = useRef<HTMLDivElement | null>(null);
  const workspaceSectionRef = useRef<HTMLElement | null>(null);

  const allTutorials = useMemo(() => [...guidedTutorials] as Tutorial[], []);
  const { questionAssistant } = useMemo(() => landingPageMockData, []);

  const greetingName = useMemo(
    () => getFirstGreetingName(currentUser?.userDisplayName, currentUser?.name),
    [currentUser?.name, currentUser?.userDisplayName],
  );
  const {
    workspaceItemsWithPresence,
    workspacesOrderedByLastUpdate,
    setCurrentWorkspaceId,
    getWorkspaceById,
    workspaces,
  } = useWorkspace();

  const {
    captureResultOptionsForHistory,
    clearCapturedResultOptions,
    appendAnswerToHistory,
  } = useLandingPageHistory();

  const getWorkspaceDisplayInfo = useCallback(
    (workspaceId: string | undefined | null) => {
      if (!workspaceId) return {};
      const workspace = getWorkspaceById(workspaceId);
      if (workspace) {
        return { name: workspace.title, type: workspace.workspaceType };
      }
      for (const group of workspaceItemsWithPresence) {
        const match = group.items.find((item) => item.id === workspaceId);
        if (match) return { name: match.title };
      }
      return {};
    },
    [getWorkspaceById, workspaceItemsWithPresence],
  );

  const {
    lastResultOptions,
    isOpeningInChat,
    suggestedActionProgress,
    openInChatWithProgress,
    executeOption,
    handleQuestionSubmit,
    handleChatClick,
    handleSuggestedActionOpenInChat,
    handleSuggestedActionSelect,
    suggestedActionsData,
  } = useLandingPageWhatToDo({
    getAccessToken,
    setCurrentWorkspaceId,
    workspaces,
    getWorkspaceDisplayInfo,
    captureResultOptionsForHistory,
    clearCapturedResultOptions,
  });

  const {
    showSessionTemplateModal,
    isApplyingSessionTemplate,
    handleAskSessionTemplateChoice,
    handleWorkspaceSessionTemplateChoice,
  } = useLandingPageSessionTemplate({
    userId: currentUser?.userId,
    userName: currentUser?.name,
    userDisplayName: currentUser?.userDisplayName,
    handleChatClick,
  });

  const {
    tutorialProgress,
    handleTutorialStart,
    handleTutorialStepComplete,
    handleTutorialComplete,
    handleTutorialProgressUpdate,
  } = useLandingPageTutorials(allTutorials);

  const recentItemsWithAvatars = useMemo(() => {
    const itemById = new Map(
      workspaceItemsWithPresence.flatMap((g) =>
        g.items.map((item) => [item.id, item] as const),
      ),
    );
    return workspacesOrderedByLastUpdate.map((ws) => {
      const item = itemById.get(ws.id);
      return item
        ? { ...item, lastUsed: ws.time }
        : {
            id: ws.id,
            title: ws.title,
            lastUsed: ws.time,
            workspaceType: ws.workspaceType,
          };
    });
  }, [workspaceItemsWithPresence, workspacesOrderedByLastUpdate]);

  const workspaceRecentActivity = useLandingPageWorkspaceActivity({
    isCompactTheme,
    recentItemsWithAvatars,
    getAccessToken,
  });

  const recentItemsForDisplay = useMemo(() => {
    return recentItemsWithAvatars.map((item) => {
      const recent = workspaceRecentActivity[item.id];
      if (!recent) return item;
      return {
        ...item,
        recentActivityAction: recent.action,
        recentActivityEntityType: recent.entityType,
        recentActivityTimestamp: recent.timestamp,
      };
    });
  }, [recentItemsWithAvatars, workspaceRecentActivity]);

  const recentItemsTitle = isCompactTheme
    ? 'Recent Activity'
    : 'Your Recent Items';
  const recentItemsVariant = isCompactTheme ? 'activity' : 'default';
  const recentWorkspaceItems = isCompactTheme
    ? recentItemsForDisplay
    : recentItemsWithAvatars;

  useEffect(() => {
    setCurrentWorkspaceId(null);
  }, [setCurrentWorkspaceId]);

  const handleKeywordMatch = useCallback(
    (_keywords: string[], action: string, route?: string) => {
      if (
        route === WORKSPACE_CREATE_ROUTE ||
        action === CREATE_WORKSPACE_ACTION
      ) {
        setIsModalOpen(true);
        return;
      }
      if (route) navigate(route);
    },
    [navigate],
  );

  const handleWorkspaceClick = useCallback(
    (workspaceId: string) => {
      navigate(`/workspace/${workspaceId}`);
    },
    [navigate],
  );

  const handleOpenCreateWorkspace = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseCreateWorkspace = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleQuickAskIntent = useCallback(() => {
    void handleChatClick('');
  }, [handleChatClick]);

  const handleWorkspaceIntent = useCallback(() => {
    if (isCompactTheme) {
      navigate('/workspace-dashboard');
      return;
    }
    workspaceSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [isCompactTheme, navigate]);

  const handlePromptLibraryIntent = useCallback(() => {
    navigate('/workspace-dashboard', {
      state: { scrollTo: 'visualPrompts' },
    });
  }, [navigate]);

  const handleOpenInChatFromChip = useCallback(
    async (params: {
      workspaceId: string;
      promptText: string;
      defaultAskPromptId?: string;
    }) => {
      await openInChatWithProgress(params);
    },
    [openInChatWithProgress],
  );

  const handleOpenInChatFromChipError = useCallback(
    (error: Error) => {
      console.error('[LandingPage] Failed to open Chat from chip:', error);
      handleError(error, 'Failed to open Chat session. Please try again.');
    },
    [handleError],
  );

  const renderResultOptionsChips = useCallback(
    (options: typeof lastResultOptions, headerText?: string) => (
      <LandingPageResultOptionsChips
        options={options}
        headerText={headerText}
        getWorkspaceDisplayInfo={getWorkspaceDisplayInfo}
        onExecuteOption={executeOption}
        onOpenInChat={handleOpenInChatFromChip}
        onOpenInChatError={handleOpenInChatFromChipError}
        isOpeningInChat={isOpeningInChat}
      />
    ),
    [
      getWorkspaceDisplayInfo,
      executeOption,
      handleOpenInChatFromChip,
      handleOpenInChatFromChipError,
      isOpeningInChat,
    ],
  );

  const sharedQuestionAssistantProps = {
    enableKeywordExtraction: false,
    onQuestionSubmit: handleQuestionSubmit,
    onKeywordMatch: handleKeywordMatch,
    onAnswerReceived: appendAnswerToHistory,
    tutorials: allTutorials,
    tutorialProgress,
    onTutorialStart: handleTutorialStart,
    onTutorialStepComplete: handleTutorialStepComplete,
    onTutorialComplete: handleTutorialComplete,
    onTutorialProgressUpdate: handleTutorialProgressUpdate,
    tutorialSessionStorageKey: LANDING_PAGE_TUTORIAL_SESSION_STORAGE_KEY,
    onSecondaryClick: handleChatClick,
  } as const;

  // ============= Render =============

  return (
    <PageContainer>
      <ContentSectionContainer $isCompact={isCompactTheme}>
        {showSessionTemplateModal && (
          <LandingPageSessionTemplateModal
            isApplying={isApplyingSessionTemplate}
            onAskSessionTemplate={() => {
              void handleAskSessionTemplateChoice();
            }}
            onWorkspaceSessionTemplate={handleWorkspaceSessionTemplateChoice}
          />
        )}

        {questionAssistant && (
          <QuestionAssistantContainer
            $isCompact={isCompactTheme}
            data-tour="lp-question-assistant-section"
            ref={questionAssistantRef}
          >
            {suggestedActionProgress && !isCompactTheme ? (
              <SuggestedActionProgressStrip
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <LoadingSpinner size="small" />
                {suggestedActionProgress}
              </SuggestedActionProgressStrip>
            ) : null}

            {isCompactTheme ? (
              <AssistantFeatureCard>
                <QuestionAssistant
                  key="compact-qa"
                  {...({
                    ...sharedQuestionAssistantProps,
                    themeMode: 'compact',
                    heroGreeting: `Welcome back, ${greetingName}.`,
                    heroDescription:
                      'Not sure what to do? Ask directly for quick help, or get guided actions that open the right workspace tools for you.',
                    heroActions: [
                      {
                        title: 'Go to Ask Session',
                        action: handleQuickAskIntent,
                      },
                      {
                        title: 'Go to Workspace Dashboard',
                        action: handleWorkspaceIntent,
                      },
                      {
                        title: 'Go to Guided Prompts',
                        action: handlePromptLibraryIntent,
                      }
                    ],
                    title: '',
                    placeholder: 'How can I help you today?',
                    submitButtonText: 'Seek Assistance',
                    secondaryButtonText: 'Ask a question',
                    enableTutorialDiscovery: true,
                    showTutorialSuggestions: true,
                    enableHistory: false,
                    suggestedActionsData,
                    onSuggestedActionSelect: handleSuggestedActionSelect,
                    onSuggestedActionOpenInChat: (prompt: string) => {
                      void handleSuggestedActionOpenInChat(prompt);
                    },
                    isSuggestedActionOpenInChatLoading: isOpeningInChat,
                    suggestedActionExecutionLoading:
                      suggestedActionProgress != null,
                    suggestedActionExecutionStatus: suggestedActionProgress,
                  } as ExtendedQuestionAssistantProps)}
                />
              </AssistantFeatureCard>
            ) : (
              <QuestionAssistant
                key="default-qa"
                {...({
                  ...sharedQuestionAssistantProps,
                  themeMode: 'default',
                  title: 'What would you like to achieve using LitiumTW?',
                  placeholder: 'Type your question here...',
                  submitButtonText: 'Submit Question',
                  secondaryButtonText: 'Ask',
                  enableTutorialDiscovery: false,
                  showTutorialSuggestions: false,
                  enableHistory: false,
                  suggestedActionsContent:
                    renderResultOptionsChips(lastResultOptions) ?? undefined,
                } as ExtendedQuestionAssistantProps)}
              />
            )}
          </QuestionAssistantContainer>
        )}

        <Spacer size={2} />

        {!isCompactTheme && (
          <>
            <WorkspaceSection
              $isCompact={isCompactTheme}
              ref={workspaceSectionRef}
            >
              <ContentSection themeMode={themeMode}>
                <WorkspaceList
                  data-tour="lp-create-workspace-list"
                  {...{
                    title: 'Create a New Workspace',
                    groups:
                      workspaceItemsWithPresence.length > 0
                        ? workspaceItemsWithPresence
                        : [],
                    listLessTab: 'Private',
                    enableSearch: true,
                    titleAsButton: true,
                    enablePagination: true,
                    enableTabs: true,
                    itemsPerPage: 5,
                    titleButtonProps: { variant: 'outlineHover' },
                    searchPlaceholder: 'Search workspaces...',
                    onCreateNew: handleOpenCreateWorkspace,
                    onItemClick: (item) => handleWorkspaceClick(item.id),
                    showMemberPresence: false,
                  }}
                />

                <WorkspaceList
                  {...{
                    title: recentItemsTitle,
                    variant: recentItemsVariant,
                    enablePagination: true,
                    itemsPerPage: 5,
                    enableSearch: true,
                    items: recentWorkspaceItems,
                    onItemClick: (item) => handleWorkspaceClick(item.id),
                    showMemberPresence: false,
                  }}
                />
              </ContentSection>
            </WorkspaceSection>

            <CreateWorkspace
              isOpen={isModalOpen}
              onClose={handleCloseCreateWorkspace}
            />
          </>
        )}
      </ContentSectionContainer>
    </PageContainer>
  );
};
